/**
 * Side-channel callbacks that links and other Apollo-layer code invoke when
 * something interesting happens on a request. Add a new key here when you
 * need a new hook into the request lifecycle — links read the latest version
 * via `getApolloHandlers` so descendants of `ApolloProvider` can update them
 * without recreating the client.
 *
 * NOTE: this is process-global, last-writer-wins state. Each handler key is
 * expected to have a single consumer (e.g. the app's one `AuthProvider`
 * registering `onAuthError`). Registering the same key from two live
 * components will clobber the first registration.
 */
export interface ApolloHandlers {
  /** Called when any operation returns a JWT/auth error. */
  onAuthError: () => void
}

const defaultApolloHandlers: ApolloHandlers = {
  onAuthError: () => undefined,
}

const handlers: ApolloHandlers = { ...defaultApolloHandlers }

export function getApolloHandlers(): ApolloHandlers {
  return handlers
}

export function setApolloHandler<K extends keyof ApolloHandlers>(
  key: K,
  handler: ApolloHandlers[K],
): void {
  handlers[key] = handler
}

export function resetApolloHandler<K extends keyof ApolloHandlers>(key: K): void {
  handlers[key] = defaultApolloHandlers[key]
}
